/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the component responsible for providing the theme to the rest
 * of the application.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

/**
 * Hooks to use state and create side effects.
 */
import { useState, useEffect } from 'react';

/**
 * Material UI ThemeProvider component.
 */
import { ThemeProvider as MuiThemeProvider } from '@material-ui/styles';

/**
 * Material UI CssBaseline component. (Resets CSS to a baseline between browsers).
 */
import { CssBaseline } from '@material-ui/core';

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * hook used to get access to the theme manager.
 */
import useThemeManagerContext from 'src/hooks/useThemeManagerContext';

/**
 * Typings for the theme.
 */
import { ITheme } from '../style/theme';

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const ThemeProvider: React.FunctionComponent = ({
    children
}) => {
    // Get the theme manager.
    const themeManager = useThemeManagerContext();

    // Get the current theme.
    const [ theme, setTheme ] = useState<ITheme>(() => themeManager.getCurrent());

    // Subscribe to updates from the theme manager and update theme when required.
    useEffect(() => {
        const unsubscribe = themeManager.subscribe((newTheme) => {
            setTheme(newTheme);
        })

        return () => {
            unsubscribe();
        }
    }, [])

    // No theme has been loaded, do not display the application.
    if (!theme) {
        return null;
    }

    return (
        <>
            <CssBaseline />
            <MuiThemeProvider theme={theme}>
                {children}
            </MuiThemeProvider>
        </>
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Exports - Default
 * ---------------------------------------------------------------------------------
 */

export default ThemeProvider;


// WEBPACK FOOTER //
// ./src/components/ThemeProvider.tsx